import { InlineMath } from "react-katex";
import { constructInputWithSpecialExpressionList, checkAnswerState } from "../../../utilization/CatchtivityUtilization";
import i18n from "../../../language/i18n";
import { useEffect, useState, useRef } from "react";
import { shuffleArray } from "../../../utilization/AppUtilization";
import MediaDropdown from "../../dropdowns/MediaDropdown";
import { IDropdownActivityMaterialProps } from "../../../properties/ActivityProperties";
import ShowMaterialMediaByContentType from "./ShowMaterialMediaByContentType";
import { dropdownActivityMaterialContentStyles } from "./DropdownActivityMaterialContentStyles";
import { resolveMaterialMediaType } from "../../../utilization/StorageUtilization";
import useAnchoredMenu from "../../../hooks/useAnchoredMenu";

// ---------------------------------------------------------------------------
// Helper: render a text value through the special-expression pipeline (math,
// bold, underline) — same approach used by the rest of the material renderers.
// ---------------------------------------------------------------------------
const RenderInlineText = ({ value }: { value: string }) => {
  const parts = constructInputWithSpecialExpressionList(value);
  return (
    <>
      {parts.map((part, i) => (
        <span
          key={i}
          className={[part.isBold ? "mc-bold" : "", part.isUnderline ? "mc-underline" : ""].join(" ").trim() || undefined}
        >
          {part.isEquation ? (
            <span className="mc-xl">
              <InlineMath math={part.value} />
            </span>
          ) : (
            part.value
          )}
        </span>
      ))}
    </>
  );
};

// ---------------------------------------------------------------------------
// InlinePill — one dropdown blank rendered as the Playful Frost glass pill.
//   • editable (canAnswer && not isPreview): tappable pill + popover list
//   • graded (isPreview + has answer): coloured chip, correct-answer hint below
//   • readonly (showCorrectAnswer or !canAnswer): static chip
// ---------------------------------------------------------------------------
interface InlinePillProps {
  materialKey: string;
  correctKey: string;
  options: string[];           // shuffled once by the parent
  currentAnswer: string;       // value from displayAnswerMap
  canAnswer: boolean;
  isPreview: boolean;
  showCorrectAnswer: boolean;
  contentType: string;
  uniqueValue: string;
  index: number;
  onChange: (key: string, value: string) => void;
}

const InlinePill = ({
  materialKey,
  correctKey,
  options,
  currentAnswer,
  canAnswer,
  isPreview,
  showCorrectAnswer,
  contentType,
  uniqueValue,
  index,
  onChange,
}: InlinePillProps) => {
  const [open, setOpen] = useState(false);
  // Outside click / Escape close it, it flips up near the bottom of the card
  // and scrolls into view (see the hook).
  const {
    anchorRef: wrapRef,
    menuRef,
    openUp,
  } = useAnchoredMenu<HTMLSpanElement, HTMLSpanElement>(open, () =>
    setOpen(false),
  );

  const hasAnswer = !!currentAnswer && currentAnswer !== "DEFAULT_OPTION";

  // Graded state — per V4, an unanswered blank stays NEUTRAL when checked.
  const answerState = isPreview && hasAnswer
    ? checkAnswerState(correctKey, currentAnswer)
    : null;

  const isCorrect = answerState === "CORRECT" || showCorrectAnswer;
  const isWrong = answerState === "INCORRECT" && !showCorrectAnswer;

  // Whether the user can interact with this blank
  const editable = canAnswer && !isPreview && !showCorrectAnswer;

  const handlePillClick = () => {
    if (!editable) return;
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
  };

  const handleOptionClick = (value: string) => {
    onChange(materialKey, value);
    setOpen(false);
  };

  // --- TEXT content type ---
  if (contentType === "TEXT") {
    // Build the pill class
    const pillClass = [
      "dds2-sel",
      !hasAnswer ? "dds2-empty" : "",
      isCorrect ? "dds2-correct" : "",
      isWrong ? "dds2-wrong" : "",
    ].filter(Boolean).join(" ");

    // Displayed value: show correctKey when revealing answer, otherwise show selection
    const displayValue = showCorrectAnswer
      ? correctKey
      : hasAnswer
        ? currentAnswer
        : i18n.t("please_select");

    return (
      <span className="dds2-wrap" ref={wrapRef}>
        <button
          type="button"
          className={pillClass}
          onClick={handlePillClick}
          disabled={!editable && (isCorrect || isWrong)}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <RenderInlineText value={displayValue} />
        </button>

        {/* Correct answer hint shown below a wrong answer */}
        {isWrong && (
          <span className="dds2-fix">
            <RenderInlineText value={correctKey} />
          </span>
        )}

        {/* Popover option list */}
        {open && editable && (
          <span
            ref={menuRef}
            className={`dds2-pop${openUp ? " dds2-pop-up" : ""}`}
            role="listbox"
          >
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                role="option"
                aria-selected={currentAnswer === opt}
                className={["dds2-opt", currentAnswer === opt ? "dds2-opt-sel" : ""].filter(Boolean).join(" ")}
                onClick={() => handleOptionClick(opt)}
              >
                <RenderInlineText value={opt} />
              </button>
            ))}
          </span>
        )}
      </span>
    );
  }

  // --- Media content type, GRADED ---
  // The picker below has no graded state at all: a learner's pick rendered with
  // no right/wrong and no key, and a blank showed the "Lütfen seçiniz" prompt.
  // Graded, the blank is drawn the way the text pill is: the pick framed in
  // green/red (amber when skipped) and, unless it was right, the correct option
  // beside it under a "Doğru cevap" tag.
  if (isPreview && !showCorrectAnswer) {
    const mediaState = hasAnswer
      ? checkAnswerState(correctKey, currentAnswer) === "CORRECT"
        ? "correct"
        : "wrong"
      : "blank";
    const renderMedia = (src: string, key: string) => (
      <ShowMaterialMediaByContentType
        key={key}
        contentType={resolveMaterialMediaType(src, contentType)}
        src={src}
        canFullScreen={true}
      />
    );
    return (
      <span className="dds2-media-graded">
        <span className={`dds2-media-card is-${mediaState}`}>
          <span className="dds2-media-body">
            {hasAnswer ? (
              renderMedia(currentAnswer, `${uniqueValue}-${index}-pick`)
            ) : (
              <span className="dds2-media-empty">—</span>
            )}
          </span>
          <span className="dds2-media-mark" aria-hidden="true">
            {mediaState === "correct" ? "✓" : mediaState === "wrong" ? "✕" : "—"}
          </span>
        </span>
        {mediaState !== "correct" ? (
          <span className="dds2-media-card is-key">
            <span className="dds2-media-tag">{i18n.t("correct_label")}</span>
            <span className="dds2-media-body">
              {renderMedia(correctKey, `${uniqueValue}-${index}-key`)}
            </span>
          </span>
        ) : null}
      </span>
    );
  }

  // --- Media content type — reuse MediaDropdown (existing behaviour preserved) ---
  const placeholderEl = (
    <div className="mc-dd-placeholder">
      <div className="mc-dd-placeholder-inner">
        <span className="mc-italic">{i18n.t("please_select")}</span>
      </div>
    </div>
  );

  const answerEl = !hasAnswer ? placeholderEl : (
    <ShowMaterialMediaByContentType
      key={`${uniqueValue}-${index}`}
      contentType={resolveMaterialMediaType(currentAnswer, contentType)}
      src={currentAnswer}
      canFullScreen={false}
    />
  );

  return (
    <span className="dds2-wrap">
      <MediaDropdown
        id={materialKey}
        answer={canAnswer ? answerEl : placeholderEl}
        optionList={options.map((opt: any, i: number) => ({
          id: i,
          media: (
            <div key={i}>
              <ShowMaterialMediaByContentType
                key={`${uniqueValue}-media-${i}`}
                contentType={resolveMaterialMediaType(opt, contentType)}
                src={opt}
                canFullScreen={false}
              />
            </div>
          ),
          onClick: (e: any) => {
            onChange(materialKey, e.target.currentSrc);
          },
        }))}
      />
    </span>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const DropdownActivityMaterialContent = ({
  uniqueValue,
  answerMap,
  materialMap,
  contentMap,
  checkCanAnswerQuestion,
  onChange,
  isPreview,
  showCorrectAnswer,
}: IDropdownActivityMaterialProps) => {
  // displayAnswerMap: when showCorrectAnswer, replace every answer with its correct key
  const [displayAnswerMap, setDisplayAnswerMap] = useState<any>(answerMap);

  useEffect(() => {
    if (showCorrectAnswer) {
      const correctAnswerMap: any = {};
      Object.keys(materialMap).forEach((materialMapKey) => {
        correctAnswerMap[materialMapKey] = Object.keys(materialMap[materialMapKey])[0];
      });
      setDisplayAnswerMap(correctAnswerMap);
    } else {
      setDisplayAnswerMap(answerMap);
    }
  }, [showCorrectAnswer, answerMap, materialMap]);

  // Options shuffled once PER ACTIVITY (content-keyed) — stable across
  // re-renders, fresh for a new activity on the same mounted component.
  const buildShuffledOptions = () => {
    const map: Record<string, string[]> = {};
    Object.keys(materialMap).forEach((key) => {
      const correctKey = Object.keys(materialMap[key])[0];
      map[key] = shuffleArray([...(materialMap[key][correctKey] || [])]);
    });
    return map;
  };
  const materialMapKey = JSON.stringify(materialMap);
  const [shuffledOptions, setShuffledOptions] = useState<Record<string, string[]>>(buildShuffledOptions);
  const shuffleKeyRef = useRef<string>(materialMapKey);
  useEffect(() => {
    if (shuffleKeyRef.current === materialMapKey) return;
    shuffleKeyRef.current = materialMapKey;
    setShuffledOptions(buildShuffledOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialMapKey]);

  const handleChange = (key: string, value: string) => {
    onChange(answerMap, key, value);
  };

  const canAnswer = checkCanAnswerQuestion();

  return (
    <>
      <div className="dds2-root">
        {Object.keys(displayAnswerMap).map((materialKey: string, index: number) => {
          const correctKey = Object.keys(materialMap[materialKey])[0];
          const options = shuffledOptions[materialKey] || materialMap[materialKey][correctKey] || [];
          const currentAnswer = displayAnswerMap[materialKey];

          return (
            <div key={materialKey} className="dds2-row">
              <span className="dds2-num">{parseFloat(materialKey) + 1}.</span>
              <div className="dds2-field">
                <InlinePill
                  materialKey={materialKey}
                  correctKey={correctKey}
                  options={options}
                  currentAnswer={currentAnswer}
                  canAnswer={canAnswer}
                  isPreview={!!isPreview}
                  showCorrectAnswer={!!showCorrectAnswer}
                  contentType={contentMap.type}
                  uniqueValue={uniqueValue}
                  index={index}
                  onChange={handleChange}
                />
              </div>
            </div>
          );
        })}
      </div>
      <style>{dropdownActivityMaterialContentStyles}</style>
    </>
  );
};

export default DropdownActivityMaterialContent;
